import React from 'react';
import {useQuery} from '@tanstack/react-query';

import {fetchPersonnelAvatar} from '@/entities/personnel/avatar';
import {Avatar} from '@/shared/ui/avatar';
import type {StatusTone} from '@/shared/ui/status';

/**
 * Лицо сотрудника по его `record_id` — одна точка на весь кабинет.
 *
 * До этого лица показывались двумя разными способами: список людей отдавал
 * `<Image>` ссылку на `/control/personnel/{id}/avatar`, а лента доступа тянула
 * ЦЕЛИКОМ карточку человека ради поля `avatar_base64`. Первый способ не работает в
 * браузере вовсе (заголовки `<img>` не несёт — разбор в `entities/personnel/avatar.ts`),
 * второй качает килобайты полей ради одной картинки. Здесь один способ, который верен
 * на обеих платформах, и один ключ кэша, поэтому одно и то же лицо в отчёте, в
 * ведомости и в ленте — это один запрос, а не три.
 *
 * Нет лица, нет права смотреть персонал, нет сети — везде инициалы: `Avatar` уже умеет
 * это, а {@link fetchPersonnelAvatar} возвращает `null` вместо ошибки на `404` и `403`.
 * Строка списка людей не должна уметь «сломаться».
 */

/** Лицо меняется раз в жизни записи, а список перерисовывается постоянно. */
const AVATAR_STALE_MS = 30 * 60_000;

export function PersonAvatar({
  companyId,
  recordId,
  name,
  size = 40,
  badgeTone,
  /**
   * `revision` карточки, если он под рукой. Он не участвует в запросе — тот идёт через
   * клиент, а не через кэш картинок браузера, — но участвует в КЛЮЧЕ: перезапись лица
   * поднимает ревизию, и кэш обязан заметить это сам, не дожидаясь получаса.
   */
  revision,
}: {
  companyId: string;
  /** Пусто — человека нет (неопознанный проход): сразу инициалы, без запроса. */
  recordId?: string | null;
  name: string;
  size?: number;
  badgeTone?: StatusTone | null;
  revision?: number | null;
}) {
  const query = useQuery({
    queryKey: ['personnel-avatar', companyId, recordId, revision ?? null],
    queryFn: () => fetchPersonnelAvatar(companyId, recordId as string),
    enabled: !!recordId,
    staleTime: AVATAR_STALE_MS,
    gcTime: AVATAR_STALE_MS,
    // Лица нет — это ответ, а не сбой: повторять его нечего.
    retry: false,
  });

  return (
    <Avatar
      name={name}
      uri={query.data ?? null}
      size={size}
      badgeTone={badgeTone}
    />
  );
}
